import { supabase } from '../api/supabase';
import type { DetailedDriverBreakdown } from '../types';

/**
 * Calculate daily rate from monthly rate based on the number of days in the given month
 */
const calculateDailyRate = (monthlyRate: number, date: string): number => {
  const targetDate = new Date(date);
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();
  
  // Get the number of days in the month
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // Calculate daily rate = monthly rate / days in month
  return Math.round((monthlyRate / daysInMonth) * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculate rent for a date range using monthly rates and proper daily calculations
 */
const calculateRentForPeriod = (monthlyRate: number, startDate: string, endDate: string): { totalRent: number, details: string[] } => {
  let totalRent = 0;
  let details: string[] = [];
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  let currentDate = new Date(start);
  
  while (currentDate <= end) {
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    // Determine actual period within this month
    const periodStart = currentDate > monthStart ? currentDate : monthStart;
    const periodEnd = end < monthEnd ? end : monthEnd;
    
    // Calculate days in this month's period
    const periodDays = Math.ceil((periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // Calculate daily rate for this specific month
    const dailyRate = calculateDailyRate(monthlyRate, periodStart.toISOString());
    const monthRent = periodDays * dailyRate;
    
    totalRent += monthRent;
    
    const monthName = periodStart.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    details.push(`${monthName}: ${periodDays} days @ AED ${dailyRate}/day = AED ${monthRent.toFixed(2)}`);
    
    // Move to next month
    currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
  }
  
  return { totalRent: Math.round(totalRent * 100) / 100, details };
};

/**
 * Get detailed breakdown of all driver charges and income
 * Shows exactly why each amount is calculated
 */
export async function getDetailedDriverBreakdown(
  driverId: string, 
  fromDate: string | null = null, 
  toDate: string | null = null
): Promise<DetailedDriverBreakdown> {
  try {
    const breakdown: DetailedDriverBreakdown = {
      rent: {
        total: 0,
        details: []
      },
      salik: {
        total: 0,
        details: []
      },
      fines: {
        total: 0,
        details: []
      },
      income: {
        total: 0,
        details: []
      },
      other: {
        total: 0,
        details: []
      }
    };

    // 1. DETAILED RENT BREAKDOWN
    console.log('🏠 Analyzing rent breakdown...');
    
    // Get current vehicle assignments with ONLY monthly rates
    const { data: currentAssignments } = await supabase
      .from('assignment_history')
      .select(`
        id,
        vehicle_id,
        assignment_date,
        return_date,
        status,
        vehicles(
          license_plate,
          make_model,
          monthly_rental_rate,
          monthly_rental_rate_incl_vat
        )
      `)
      .eq('primary_driver_id', driverId)
      .order('assignment_date', { ascending: true });

    if (currentAssignments) {
      for (const assignment of currentAssignments) {
        const startDate = assignment.assignment_date;
        const endDate = assignment.return_date || new Date().toISOString().split('T')[0];
        
        // Get monthly rate (ONLY source of truth)
        const monthlyRate = assignment.vehicles?.monthly_rental_rate || 
                           assignment.vehicles?.monthly_rental_rate_incl_vat || 
                           2850; // fallback monthly rate (AED 2850/month)
        
        // Calculate rent using monthly rate and proper daily calculations
        const { totalRent, details } = calculateRentForPeriod(monthlyRate, startDate, endDate);
        
        const periodText = assignment.return_date 
          ? `${assignment.assignment_date} to ${assignment.return_date}`
          : `${assignment.assignment_date} to ongoing`;
          
        console.log('🏠 Monthly-based rent calculation:', {
          driverId,
          vehicle: `${assignment.vehicles?.license_plate} - ${assignment.vehicles?.make_model}`,
          startDate: assignment.assignment_date,
          endDate: assignment.return_date || 'ongoing',
          monthlyRate,
          totalRent,
          details,
          monthlyRateSource: assignment.vehicles?.monthly_rental_rate ? 'vehicle' : 'fallback'
        });
          
        breakdown.rent.details.push({
          description: `Monthly rate: AED ${monthlyRate} - ${details.join(', ')}`,
          vehicle: `${assignment.vehicles?.license_plate} - ${assignment.vehicles?.make_model}`,
          amount: totalRent,
          period: periodText,
          days: Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1,
          rate: monthlyRate // Store monthly rate as the rate
        });
        breakdown.rent.total += totalRent;
      }
    }

    // Get assignment history (note: assignment_history table may still have legacy daily_rate column)
    const { data: historyAssignments } = await supabase
      .from('assignment_history')
      .select(`
        id,
        vehicle_id,
        assignment_date,
        return_date,
        monthly_rent_rate,
        total_days,
        total_amount,
        status,
        vehicles(
          license_plate,
          make_model,
          monthly_rental_rate
        )
      `)
      .eq('primary_driver_id', driverId)
      .order('assignment_date', { ascending: true });

    if (historyAssignments) {
      for (const assignment of historyAssignments) {
        // Fix date logic - ensure start date is before end date
        const startDate = assignment.assignment_date;
        const endDate = assignment.return_date || new Date().toISOString().split('T')[0];
        
        // Skip invalid date ranges
        if (new Date(startDate) > new Date(endDate)) {
          console.log('⚠️ Skipping invalid date range:', { startDate, endDate, assignmentId: assignment.id });
          continue;
        }
        
        const periodText = assignment.return_date 
          ? `${startDate} to ${endDate}`
          : `${startDate} to ongoing`;
          
        // Use total_amount if available, otherwise calculate from monthly rate if available
        let rentAmount = assignment.total_amount || 0;
        let rateDisplay = assignment.monthly_rent_rate || 0;
        
        // If we have monthly rate and days, recalculate more accurately
        if (assignment.vehicles?.monthly_rental_rate && assignment.total_days) {
          const dailyRate = calculateDailyRate(
            assignment.vehicles.monthly_rental_rate, 
            startDate
          );
          rentAmount = dailyRate * assignment.total_days;
          rateDisplay = assignment.vehicles.monthly_rental_rate;
        }
          
        breakdown.rent.details.push({
          description: `${assignment.total_days} days from ${periodText} (Historical Assignment)`,
          vehicle: `${assignment.vehicles?.license_plate} - ${assignment.vehicles?.make_model}`,
          amount: rentAmount,
          period: periodText,
          days: assignment.total_days,
          rate: rateDisplay
        });
        breakdown.rent.total += rentAmount;
      }
    }

    // 2. DETAILED SALIK BREAKDOWN
    console.log('🛣️ Analyzing Salik breakdown...');
    
    try {
      // Using salik_tolls table as per DATABASE_COMPLETE_STRUCTURE.md
      const { data: salikTransactions } = await supabase
        .from('salik_tolls')
        .select('*')
        .eq('driver_id', driverId)
        .order('transaction_date', { ascending: true });

      if (salikTransactions) {
        // Group by date and amount
        const salikGroups: { [key: string]: { gateType: string; amount: number; count: number; dates: string[] } } = {};
        
        for (const transaction of salikTransactions) {
          const amount = Math.abs(transaction.amount);
          const date = transaction.transaction_date;
          const gateName = transaction.toll_gate_name || 'Unknown Gate';
          
          // Determine gate type based on amount
          let gateType = gateName;
          if (amount === 4) gateType += ' (Standard)';
          else if (amount === 6) gateType += ' (Premium)';
          else if (amount === 5) gateType += ' (Express)';
          else gateType += ` (AED ${amount})`;
          
          const key = `${gateType}-${amount}`;
          
          if (!salikGroups[key]) {
            salikGroups[key] = {
              gateType,
              amount,
              count: 0,
              dates: []
            };
          }
          
          salikGroups[key].count++;
          salikGroups[key].dates.push(date);
          breakdown.salik.total += amount;
        }
        
        // Convert to breakdown details
        Object.values(salikGroups).forEach(group => {
          const dateRange = group.dates.length > 1 
            ? `${group.dates[0]} to ${group.dates[group.dates.length - 1]}`
            : group.dates[0];
            
          breakdown.salik.details.push({
            description: `${group.count} ${group.gateType} toll${group.count > 1 ? 's' : ''} @ AED ${group.amount} each`,
            amount: group.count * group.amount,
            count: group.count,
            rate: group.amount,
            period: dateRange
          });
        });
      }
    } catch (error) {
      console.log('ℹ️ Salik tolls table error:', error);
    }

    // 3. DETAILED FINES BREAKDOWN
    console.log('🚨 Analyzing fines breakdown...');
    
    try {
      // Using traffic_fines table as per DATABASE_COMPLETE_STRUCTURE.md
      const { data: fineTransactions } = await supabase
        .from('traffic_fines')
        .select('*')
        .eq('driver_id', driverId)
        .order('issue_date', { ascending: true });

      if (fineTransactions) {
        // Group by fine type
        const fineGroups: { [key: string]: { type: string; count: number; total: number; dates: string[] } } = {};
        
        for (const transaction of fineTransactions) {
          const amount = Math.abs(transaction.amount_after_discount || transaction.total_amount || 0);
          const fineNumber = transaction.fine_number || 'Unknown';
          
          // Use fine number as unique identifier for grouping
          const fineType = `Fine ${fineNumber}`;
          
          if (!fineGroups[fineType]) {
            fineGroups[fineType] = {
              type: fineType,
              count: 0,
              total: 0,
              dates: []
            };
          }
          
          fineGroups[fineType].count++;
          fineGroups[fineType].total += amount;
          fineGroups[fineType].dates.push(transaction.issue_date);
          breakdown.fines.total += amount;
        }
        
        // Convert to breakdown details
        Object.values(fineGroups).forEach(group => {
          const dateRange = group.dates.length > 1 
            ? `${group.dates[0]} to ${group.dates[group.dates.length - 1]}`
            : group.dates[0];
            
          breakdown.fines.details.push({
            description: `${group.type} - AED ${group.total}`,
            amount: group.total,
            count: group.count,
            type: group.type,
            period: dateRange
          });
        });
      }
    } catch (error) {
      console.log('ℹ️ Traffic fines table error:', error);
    }

    // 4. DETAILED INCOME BREAKDOWN
    console.log('💰 Analyzing income breakdown...');
    
    // Get driver balance data for fallback income totals
    let fallbackCareemIncome = 0;
    let fallbackUberIncome = 0;
    
    try {
      const { data: balanceData } = await supabase
        .from('driver_balances')
        .select('total_careem_income, total_uber_income')
        .eq('driver_id', driverId)
        .single();
      
      if (balanceData) {
        fallbackCareemIncome = balanceData.total_careem_income || 0;
        fallbackUberIncome = balanceData.total_uber_income || 0;
        console.log(`📊 Fallback totals - Careem: ${fallbackCareemIncome}, Uber: ${fallbackUberIncome}`);
      }
    } catch (error) {
      console.log('ℹ️ Could not fetch driver balance fallback data:', error);
    }
    
    // Careem Income from careem_income_raw table
    let careemIncomeFromRaw = 0;
    try {
      const { data: careemIncome } = await supabase
        .from('careem_income_raw')
        .select('*')
        .eq('driver_id', driverId)
        .order('report_week_start', { ascending: true });

      console.log(`🔍 Found ${careemIncome?.length || 0} Careem income records for driver`);
      
      if (careemIncome && careemIncome.length > 0) {
        // Group by week period
        const weeklyGroups: { [key: string]: { week: string; careem: number; records: number } } = {};
        
        for (const income of careemIncome) {
          const weekStart = income.report_week_start;
          const weekEnd = income.report_week_end;
          const amount = parseFloat(income.total_earnings) || 0;
          const weekPeriod = `${weekStart} to ${weekEnd}`;
          
          if (!weeklyGroups[weekPeriod]) {
            weeklyGroups[weekPeriod] = {
              week: weekPeriod,
              careem: 0,
              records: 0
            };
          }
          
          weeklyGroups[weekPeriod].careem += amount;
          weeklyGroups[weekPeriod].records++;
          careemIncomeFromRaw += amount;
        }
        
        Object.values(weeklyGroups).forEach(group => {
          breakdown.income.details.push({
            description: `Careem income for ${group.week} - ${group.records} record${group.records > 1 ? 's' : ''}`,
            amount: group.careem,
            source: 'Careem',
            period: group.week,
            trips: group.records
          });
        });
        
        breakdown.income.total += careemIncomeFromRaw;
        console.log(`✅ Added ${careemIncomeFromRaw} from Careem raw data`);
      } else if (fallbackCareemIncome > 0) {
        // Use fallback data from driver_balances when raw data is not available
        breakdown.income.details.push({
          description: `Careem income (aggregated total)`,
          amount: fallbackCareemIncome,
          source: 'Careem',
          period: 'Aggregated data',
          trips: 0
        });
        breakdown.income.total += fallbackCareemIncome;
        console.log(`⚠️ Used fallback Careem income: ${fallbackCareemIncome}`);
      }
    } catch (error) {
      console.log('ℹ️ Careem income table error:', error);
      // Use fallback data if available
      if (fallbackCareemIncome > 0) {
        breakdown.income.details.push({
          description: `Careem income (aggregated total)`,
          amount: fallbackCareemIncome,
          source: 'Careem',
          period: 'Aggregated data',
          trips: 0
        });
        breakdown.income.total += fallbackCareemIncome;
        console.log(`🔄 Fallback Careem income after error: ${fallbackCareemIncome}`);
      }
    }

    // Uber Income from uber_income_raw table
    let uberIncomeFromRaw = 0;
    try {
      const { data: uberIncome } = await supabase
        .from('uber_income_raw')
        .select('*')
        .eq('driver_id', driverId)
        .order('report_week_start', { ascending: true });

      console.log(`🔍 Found ${uberIncome?.length || 0} Uber income records for driver`);

      if (uberIncome && uberIncome.length > 0) {
        // Group by week period
        const weeklyGroups: { [key: string]: { week: string; uber: number; records: number } } = {};
        
        for (const income of uberIncome) {
          const weekStart = income.report_week_start;
          const weekEnd = income.report_week_end;
          const amount = parseFloat(income.total_income) || 0;
          const weekPeriod = `${weekStart} to ${weekEnd}`;
          
          if (!weeklyGroups[weekPeriod]) {
            weeklyGroups[weekPeriod] = {
              week: weekPeriod,
              uber: 0,
              records: 0
            };
          }
          
          weeklyGroups[weekPeriod].uber += amount;
          weeklyGroups[weekPeriod].records++;
          uberIncomeFromRaw += amount;
        }
        
        Object.values(weeklyGroups).forEach(group => {
          breakdown.income.details.push({
            description: `Uber income for ${group.week} - ${group.records} record${group.records > 1 ? 's' : ''}`,
            amount: group.uber,
            source: 'Uber',
            period: group.week,
            trips: group.records
          });
        });
        
        breakdown.income.total += uberIncomeFromRaw;
        console.log(`✅ Added ${uberIncomeFromRaw} from Uber raw data`);
      } else if (fallbackUberIncome > 0) {
        // Use fallback data from driver_balances when raw data is not available
        breakdown.income.details.push({
          description: `Uber income (aggregated total)`,
          amount: fallbackUberIncome,
          source: 'Uber',
          period: 'Aggregated data',
          trips: 0
        });
        breakdown.income.total += fallbackUberIncome;
        console.log(`⚠️ Used fallback Uber income: ${fallbackUberIncome}`);
      }
    } catch (error) {
      console.log('ℹ️ Uber income table error:', error);
      // Use fallback data if available
      if (fallbackUberIncome > 0) {
        breakdown.income.details.push({
          description: `Uber income (aggregated total)`,
          amount: fallbackUberIncome,
          source: 'Uber',
          period: 'Aggregated data',
          trips: 0
        });
        breakdown.income.total += fallbackUberIncome;
        console.log(`🔄 Fallback Uber income after error: ${fallbackUberIncome}`);
      }
    }

    return breakdown;
    
  } catch (error) {
    console.error('Error getting detailed breakdown:', error);
    throw error;
  }
}
